import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CurrencyInput } from "@/components/CurrencyInputDecimal";

interface MarginInputRowProps {
  isDefault?: boolean;
  minQty?: number;
  marginType: "PERCENTAGE" | "AMOUNT";
  value: string;
  onMinQtyChange?: (value: number) => void;
  onMarginTypeChange: (type: "PERCENTAGE" | "AMOUNT") => void;
  onValueChange: (value: string) => void;
  onRemove?: () => void;
  disabled?: boolean;
  showQuantityInput?: boolean;
  label?: string;
}

/**
 * Shared component for rendering margin input rows.
 * Handles default margins and quantity-based margin overrides.
 * Used by both ItemMarginEditor and BOMMarginEditor.
 */
export function MarginInputRow({
  isDefault = false,
  minQty,
  marginType,
  value,
  onMinQtyChange,
  onMarginTypeChange,
  onValueChange,
  onRemove,
  disabled = false,
  showQuantityInput = false,
  label,
}: MarginInputRowProps) {
  const containerClassName = isDefault
    ? "bg-muted/50 rounded-md p-3 space-y-3"
    : "bg-accent rounded-md p-3 space-y-3";

  const headerLabelClassName = isDefault
    ? "text-xs font-medium text-muted-foreground"
    : "text-xs font-medium text-accent-foreground";

  return (
    <div className={containerClassName}>
      <div className="flex justify-between items-center">
        <span className={headerLabelClassName}>
          {label ||
            (isDefault
              ? "Default (All Quantities)"
              : `At Quantity ${minQty || 1}`)}
        </span>
        {onRemove && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRemove}
            disabled={disabled}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        )}
      </div>

      <div
        className={`grid gap-3 ${showQuantityInput ? "grid-cols-3" : "grid-cols-2"}`}
      >
        {showQuantityInput && (
          <div className="space-y-2">
            <Label className="text-xs">Quantity</Label>
            <Input
              type="number"
              min="1"
              step="1"
              value={minQty?.toString() ?? ""}
              onChange={(e) => {
                const inputValue = e.target.value;
                const numValue =
                  inputValue === "" ? undefined : parseInt(inputValue, 10);
                if (onMinQtyChange && numValue !== undefined) {
                  onMinQtyChange(numValue);
                }
              }}
              placeholder="1"
              disabled={disabled}
              className="h-8 bg-background"
            />
          </div>
        )}

        <div className="space-y-2">
          <Label className="text-xs">Type</Label>
          <Select
            value={marginType}
            onValueChange={(value: "PERCENTAGE" | "AMOUNT") =>
              onMarginTypeChange(value)
            }
            disabled={disabled}
          >
            <SelectTrigger className="h-8 bg-background">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="PERCENTAGE">Percentage</SelectItem>
              <SelectItem value="AMOUNT">Fixed Amount</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="text-xs">
            {marginType === "PERCENTAGE" ? "Percent (%)" : "Amount"}
          </Label>
          {marginType === "PERCENTAGE" ? (
            <Input
              type="number"
              min="0"
              max="99.99"
              step="0.01"
              value={value}
              onChange={(e) => onValueChange(e.target.value)}
              placeholder="0.00"
              disabled={disabled}
              className="h-8 bg-background"
            />
          ) : (
            <CurrencyInput
              value={value}
              onChange={(inputValue) => onValueChange(inputValue || "0")}
              placeholder="0.00"
              disabled={disabled}
              className="h-8 bg-background"
            />
          )}
        </div>
      </div>
    </div>
  );
}
